import { NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PanelLeftClose, PanelLeft } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { NAV_ITEMS } from "@/lib/constants";
import { useAuth } from "@/hooks/useAuth";
import { canAccessModule } from "@/lib/permissions/permissions";
import { roleLabel } from "@/lib/permissions/roles";
import { dashboardService } from "@/services/dashboardService";
import { Logo } from "./Logo";

const BADGE_KEYS = { orders: "ready_orders", inbound: "inbound_waiting", returns: "returns_pending" };

function initials(name = "", email = "") {
  const src = (name || "").trim() || email || "";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export function NavList({ collapsed = false, onNavigate }) {
  const { user } = useAuth();
  const role = user?.role;
  const summaryQuery = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: dashboardService.summary,
    enabled: !!role,
  });
  const counts = summaryQuery.data?.counts || {};
  const items = NAV_ITEMS.filter((item) => canAccessModule(role, item.key));

  return (
    <nav className="flex flex-col gap-0.5 px-3" data-testid="sidebar-nav">
      {items.map(({ key, label, to, icon: Icon }) => {
        const badge = counts[BADGE_KEYS[key]] || 0;
        const link = (
          <NavLink
            key={key}
            to={to}
            end={to === "/dashboard"}
            onClick={onNavigate}
            data-testid={`nav-${key}`}
            className={({ isActive }) =>
              cn(
                "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                collapsed && "justify-center px-0",
                isActive
                  ? "text-primary"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.span
                    layoutId="nav-active"
                    className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary/12 via-primary/8 to-transparent ring-1 ring-inset ring-primary/15"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  />
                )}
                {isActive && (
                  <span className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-primary shadow-[0_0_10px_hsl(var(--primary)/0.6)]" />
                )}
                <Icon
                  className={cn(
                    "relative z-10 h-[18px] w-[18px] shrink-0 transition-transform",
                    isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground",
                    !isActive && "group-hover:-translate-y-[1px]"
                  )}
                  strokeWidth={isActive ? 2.25 : 1.75}
                />
                {!collapsed && <span className="relative z-10 flex-1 truncate">{label}</span>}
                {badge > 0 && !collapsed && (
                  <span
                    data-testid={`nav-badge-${key}`}
                    className={cn(
                      "relative z-10 ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums",
                      isActive
                        ? "bg-primary text-primary-foreground shadow-[0_0_10px_hsl(var(--primary)/0.5)]"
                        : "bg-muted text-foreground"
                    )}
                  >
                    {badge}
                  </span>
                )}
                {badge > 0 && collapsed && (
                  <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.7)]" />
                )}
              </>
            )}
          </NavLink>
        );

        if (!collapsed) return link;
        return (
          <TooltipProvider key={key} delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>{link}</TooltipTrigger>
              <TooltipContent side="right">{label}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </nav>
  );
}

function UserCard({ collapsed }) {
  const { user } = useAuth();
  if (!user) return null;
  if (collapsed) {
    return (
      <div className="flex justify-center">
        <Avatar className="h-9 w-9 border border-border/60 shadow-premium">
          <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-xs font-semibold text-primary-foreground">
            {initials(user.name, user.email)}
          </AvatarFallback>
        </Avatar>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-card/60 px-3 py-2.5 backdrop-blur-sm">
      <Avatar className="h-9 w-9 border border-border/60 shadow-premium">
        <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-xs font-semibold text-primary-foreground">
          {initials(user.name, user.email)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-foreground">{user.name || "Account"}</p>
        <p className="truncate text-[11px] text-muted-foreground">{roleLabel(user.role)}</p>
      </div>
    </div>
  );
}

export function Sidebar({ collapsed, onToggle }) {
  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-border/60 bg-[hsl(var(--surface-sidebar))] backdrop-blur-sm transition-[width] duration-200 lg:flex",
        collapsed ? "w-[76px]" : "w-64"
      )}
    >
      <div
        className={cn(
          "flex h-16 items-center border-b border-border/60 px-5",
          collapsed && "justify-center px-0"
        )}
      >
        <Logo collapsed={collapsed} />
      </div>

      <div className="scrollbar-thin flex-1 overflow-y-auto py-4">
        <NavList collapsed={collapsed} />
      </div>

      <div className={cn("border-t border-border/60 p-3", collapsed && "px-2")}>
        <UserCard collapsed={collapsed} />
        <button
          type="button"
          onClick={onToggle}
          data-testid="sidebar-toggle"
          className={cn(
            "mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-ring",
            collapsed && "justify-center px-0"
          )}
        >
          {collapsed ? (
            <PanelLeft className="h-[16px] w-[16px]" />
          ) : (
            <>
              <PanelLeftClose className="h-[16px] w-[16px]" />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
